import * as React from "react";
import { Link } from "@tanstack/react-router";

import { BreadcrumbItem, BreadcrumbLink, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { EffectiveDateAlert } from "@/components/ui/effective-date-alert";
import { MuiAlert } from "@/components/ui/mui-alert";
import { MuiButton } from "@/components/ui/mui-button";
import { MuiLink } from "@/components/ui/mui-link";
import { MuiSkeleton } from "@/components/ui/mui-skeleton";
import { MuiInfoOutlinedSmallIcon } from "@/components/ui/mui-spec-icons";
import { MuiTooltip } from "@/components/ui/mui-tooltip";
import { MuiTypography } from "@/components/ui/mui-typography";
import { PageContainer } from "@/components/ui/page-container";
import { SpecificationChip } from "@/components/ui/specification-chip";
import {
  bannerFor,
  breadcrumbLeaf,
  breadcrumbRoot,
  displayName,
  effectiveDateText,
  type SpecMetadataFacts,
  type VersionBanner,
} from "@/lib/spec-display";
import { formatShortUsDate } from "@/lib/format";
import type { SpecType } from "@/api/contracts/specifications";
import { communityDetailPath, specDetailPath, specListPath } from "@/lib/route-paths";
import { truncateText } from "@/lib/truncate-text";
import { cn } from "@/lib/utils";

import { SpecScrollRegionContext } from "@/hooks/spec-lines";

import { SpecLinesIslandSkeleton } from "./spec-lines-body";
import { SpecificationNotes } from "./specification-notes";

// The details page above its lines — the donor's `SpecDetailsIsland` down to
// its `HeaderContainer` (`SpecDetailsIsland/components/`), measured on the
// running donor at 1440×900 (#694):
//
//   - **the page**: `PageContainer` (24px / 40px); inside it a region that, from
//     1200px, is held to the viewport below the bars with its overflow hidden,
//     and inside that the scroll region (`Layout.tsx`): white, `px 24`, `pb 40`
//     from 900px, `overflow: auto`, held to the same height at every width;
//   - **the header stack** (`Container.tsx`): a white column, gap 8, `pt 26`,
//     above the page's other content (z 9): the breadcrumbs, the h2, then the
//     metadata line with the notes block beneath it — and then the line search
//     and the controls row, which are the lines body's toolbar (#695): the
//     `toolbar` slot, items of the same stack;
//   - beneath the stack, 16px down (the donor's `Grid container spacing={2}`),
//     the groups — `children`, #695's too. They reach the scroll region through
//     `useSpecScrollRegion`: its sticky group headers, its pagination observer's
//     root and its scroll-to-top button all bind to that one element, as the
//     donor's all bind to `Layout`'s `scrollRef`;
//   - **the banners**: at most one, floating over the top of the page.
//
// Every rule a part draws by is in `@/lib/spec-display`, where it is tested.
//
// **Two faces, one set of parts** (#1024): the container and its scroll region,
// the header stack, the heading, the metadata line and the version alert are
// exported, and the public details page composes them under the source's
// public subtractions — no breadcrumbs, no draft or published banner, fewer
// metadata parts — rather than drawing a second copy that could drift. The
// face is the rendering route's choice, never a URL test.

/** The fields of the spec summary the frame reads. */
export interface SpecDetailsSummary {
  id: number;
  name: string;
  type: SpecType;
  communityId: string | null;
  notes: string | null;
  division: { id: number; name: string } | null;
  community: { id: number; name: string; externalFullId: string } | null;
  parentVersion: {
    specificationId: number;
    specificationName: string;
    versionId: number;
    versionCode: string | null;
  } | null;
  lastPublishedVersionId: number | null;
  newestVersionId: number | null;
  versions: readonly SpecDetailsVersion[];
  /** The spec's Latest version's over-target count (#1214) — the target-cost
   *  toast's; `null` for a reader, 0 on a core or base spec. */
  overTargetCount: number | null;
}

export interface SpecDetailsVersion {
  id: number;
  versionCode: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  effectiveAt: string | null;
  linkedCommunityCount: number;
}

/** The summary and the URL's version on it, already found by the route. */
export interface SpecDetailsLoaded {
  summary: SpecDetailsSummary;
  version: SpecDetailsVersion;
}

interface SpecDetailsFrameProps {
  /**
   * `null` when the summary read failed: the header then wears the donor's
   * blank face — see `BLANK_DISPLAY_NAME`.
   */
  read: SpecDetailsLoaded | null;
  /** At the foot of the header stack: #695's search row and controls row. */
  toolbar?: React.ReactNode;
  /** Beneath the header stack: #695's groups. */
  children?: React.ReactNode;
}

/** `MAX_COMMUNITY_NAME_LENGTH` (`SpecMetadata.tsx:19`). */
const MAX_COMMUNITY_NAME_LENGTH = 35;

/**
 * The heading over a failed summary read — `SpecDisplayName`'s template over
 * the cache's blanks (`SpecDisplayName.tsx:23-30`): no name, and `isPublished`
 * undefined reads as a draft. The spaces are the template's; the page
 * collapses them.
 */
const BLANK_DISPLAY_NAME = " (Draft) ";

export function SpecDetailsFrame({ read, toolbar, children }: SpecDetailsFrameProps) {
  return (
    <>
      {read ? <SpecBanner summary={read.summary} version={read.version} /> : null}
      <SpecDetailsContainer>
        <SpecDetailsHeaderStack>
          <Breadcrumbs>
            <BreadcrumbLink>
              {/* With no summary, the donor's root crumb is a link with no
                  label to `''` — this page (`SpecDetailsBreadcrumbs.tsx:19-44`,
                  `utils/common.ts:180-203`) — and its leaf is empty, so the
                  separator alone shows. */}
              {read ? (
                <Link to={specListPath(read.summary.type)}>{breadcrumbRoot(read.summary)}</Link>
              ) : (
                <Link to="." />
              )}
            </BreadcrumbLink>
            <BreadcrumbItem>
              {read ? breadcrumbLeaf(read.summary.name, read.version) : ""}
            </BreadcrumbItem>
          </Breadcrumbs>
          <SpecDetailsHeading>
            {read ? displayName(read.summary, read.version) : BLANK_DISPLAY_NAME}
          </SpecDetailsHeading>
          {/* Every part of the line is gated on the read, so it stands empty
              (`SpecMetadata.tsx:157-169`); the notes are `null`. */}
          <SpecDetailsMetadataAndNotes
            metadata={read ? signedInMetadataFacts(read.summary, read.version) : null}
            notes={read?.summary.notes ?? null}
          />
          {toolbar}
        </SpecDetailsHeaderStack>
        {children}
      </SpecDetailsContainer>
    </>
  );
}

/**
 * The header stack (`Header/Container.tsx`): a white column, gap 8, `pt 26`,
 * above the page's other content (z 9).
 */
export function SpecDetailsHeaderStack({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative z-[9] flex w-full flex-col gap-2 bg-white pt-[26px]">{children}</div>
  );
}

/** The h2 row (`SpecDisplayName.tsx:45-47`): a column below 600px. */
export function SpecDetailsHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col flex-wrap justify-between gap-2 min-[600px]:flex-row">
      <MuiTypography variant="h2" component="h2">
        {children}
      </MuiTypography>
    </div>
  );
}

/**
 * The metadata line with the notes block beneath it — `null` facts draw the
 * line empty, as a failed signed-in read does.
 */
export function SpecDetailsMetadataAndNotes({
  metadata,
  notes,
}: {
  metadata: SpecMetadataFacts | null;
  notes: string | null;
}) {
  return (
    <div>
      {metadata ? (
        <SpecMetadata facts={metadata} />
      ) : (
        <div data-testid="spec-metadata" className={METADATA_LINE} />
      )}
      <SpecificationNotes notes={notes} />
    </div>
  );
}

/**
 * The page's frame — its container, the height-held region and the scroll
 * region — shared by the loaded page and its skeleton, as the donor's `Layout`
 * wraps both. Its ink is MUI's `text.primary`, which everything in the frame
 * inherits in the donor; the body's own is darker.
 */
export function SpecDetailsContainer({ children }: { children: React.ReactNode }) {
  const [region, setRegion] = React.useState<HTMLDivElement | null>(null);
  return (
    <PageContainer>
      <div
        id="spec-details"
        // Held beneath the app header and the spec band — the donor's
        // `calc(100vh - var(--app-header-height))`, whose one property measured
        // a bar holding both. The public face is held too, beneath the public
        // header alone (no band, so its variable falls back to 0): a named
        // break from the donor's public page, whose header publishes no
        // `--app-header-height` and whose window scrolls instead — owner-ruled
        // #1055 D1 = b, on #1027.
        className="flex flex-col min-[1200px]:max-h-[calc(100vh-var(--app-header-height,0px)-var(--spec-header-height,0px))] min-[1200px]:overflow-hidden"
      >
        <div
          ref={setRegion}
          data-testid="spec-details-scroll"
          className="text-lennar-ink flex max-h-[calc(100vh-var(--app-header-height,0px)-var(--spec-header-height,0px))] flex-col gap-4 overflow-auto bg-white px-6 min-[900px]:pb-10"
        >
          <SpecScrollRegionContext.Provider value={region}>
            {children}
          </SpecScrollRegionContext.Provider>
        </div>
      </div>
    </PageContainer>
  );
}

/**
 * The loading face — `HeaderSkeleton` (`Skeletons.tsx`): a column, gap 12,
 * `pt 24`, `pb 16`, of six rounded wave bars. Alone it is the route's pending
 * face while the summary is on its way; the lines body stands its five group
 * skeletons beneath it (`children`) for the rest of the donor's
 * `IslandSkeleton`, which covers the whole island until the first lines page
 * lands (#695).
 */
export function SpecDetailsSkeleton({ children }: { children?: React.ReactNode }) {
  const bars = ["h-6 w-1/2", "h-12 w-1/3", "h-6 w-1/2", "h-10 w-full", "h-14 w-1/2", "h-6 w-[10%]"];
  return (
    <SpecDetailsContainer>
      <div data-testid="spec-details-skeleton" className="flex flex-col gap-3 pt-6 pb-4">
        {/* A fixed list, and two bars share a size: the index is the identity. */}
        {bars.map((size, index) => (
          <MuiSkeleton key={index} variant="rounded" animation="wave" className={size} />
        ))}
      </div>
      {children}
    </SpecDetailsContainer>
  );
}

/**
 * The donor's `IslandSkeleton` (`Skeletons.tsx:7-18`): the header's bars and
 * five group skeletons, for as long as `isLoadingMetadata || isLoadingLines`
 * (`loader.ts:51`, `SpecDetailsIsland.tsx:149`) — so the route's pending face
 * while the summary loads and the page's while its first lines page does are
 * the one drawing, and nothing grows between them. The public page's too,
 * while its first lines page loads.
 */
export function SpecDetailsIslandSkeleton() {
  return (
    <SpecDetailsSkeleton>
      <SpecLinesIslandSkeleton />
    </SpecDetailsSkeleton>
  );
}

const METADATA_LINE = "flex flex-row flex-nowrap items-center gap-2 text-base leading-normal";

/**
 * The signed-in face's metadata facts: every part the summary has, the count
 * the viewed version's own and Based on a community spec's.
 */
function signedInMetadataFacts(
  summary: SpecDetailsSummary,
  version: SpecDetailsVersion,
): SpecMetadataFacts {
  const isLastPublished = version.id === summary.lastPublishedVersionId;
  const lastPublished = summary.versions.find(
    (candidate) => candidate.id === summary.lastPublishedVersionId,
  );
  return {
    division: summary.division?.name ?? null,
    publishedAt: version.isPublished ? version.publishedAt : null,
    effective: version.effectiveAt
      ? {
          effectiveAt: version.effectiveAt,
          rangeEndAt: isLastPublished ? null : (lastPublished?.effectiveAt ?? null),
          isLastPublished,
        }
      : null,
    // The viewed version's own count, and no plural rule: "1 Communities".
    communityCount: summary.type === "base" ? version.linkedCommunityCount : null,
    chip: summary.type === "community" ? null : summary.type,
    community: summary.type === "community" ? summary.community : null,
    // The donor renders Based on only when the based-on summaries are cached,
    // which for a base spec they never are (its versions carry `based_on_core`,
    // not `based_on`), so a base spec shows none although `parentVersion`
    // travels for it (#693).
    basedOn: summary.type === "community" ? summary.parentVersion : null,
  };
}

/**
 * The metadata line — `SpecMetadata` (`Header/SpecMetadata.tsx`): a row, gap 8,
 * centred and unwrapped, a ` • ` span between each part present, in this order.
 */
function SpecMetadata({ facts }: { facts: SpecMetadataFacts }) {
  const parts: { key: string; node: React.ReactNode }[] = [];

  if (facts.division) parts.push({ key: "division-name", node: facts.division });
  if (facts.publishedAt) {
    parts.push({
      key: "published-date",
      node: `Published ${formatShortUsDate(facts.publishedAt)}`,
    });
  }
  if (facts.effective) {
    parts.push({
      key: "effective-date",
      node: (
        <EffectiveDateAlert
          text={effectiveDateText(facts.effective.effectiveAt, facts.effective.rangeEndAt)}
          title="Effective Date"
          isLastPublishedVersion={facts.effective.isLastPublished}
        />
      ),
    });
  }
  if (facts.communityCount !== null) {
    parts.push({ key: "community-spec-count", node: `${facts.communityCount} Communities` });
  }
  if (facts.chip) {
    parts.push({
      key: "specification-chip",
      node: (
        <div>
          <SpecificationChip kind={facts.chip} />
        </div>
      ),
    });
  }
  if (facts.community) {
    parts.push({ key: "community-name", node: <CommunityName community={facts.community} /> });
  }
  if (facts.basedOn) {
    const parent = facts.basedOn;
    parts.push({
      key: "based-on-spec",
      node: (
        <>
          Based on:
          <MuiLink asChild underline="hover" className="pl-1">
            <Link to={specDetailPath(parent.specificationId, parent.versionId)}>
              <span className="font-bold">
                {parent.versionCode
                  ? `${parent.specificationName} (v${parent.versionCode})`
                  : parent.specificationName}
              </span>
            </Link>
          </MuiLink>
        </>
      ),
    });
  }

  return (
    <div data-testid="spec-metadata" className={METADATA_LINE}>
      {parts.map((part, index) => (
        <React.Fragment key={part.key}>
          {index > 0 ? <span> • </span> : null}
          <div data-testid={`spec-metadata-${part.key}`}>{part.node}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * "Community Name:" and the JDE glyph — the donor's community part. The name
 * links to the community page in a new tab (`SpecMetadata.tsx:96-108`: `Link
 * target='_blank' underline='hover'`, its text bold in primary), awake since
 * that page mounted (#679 Q3, #884).
 */
function CommunityName({ community }: { community: NonNullable<SpecMetadataFacts["community"]> }) {
  return (
    <div className="flex flex-row items-center gap-1">
      <p className="m-0 flex">
        Community Name:
        <MuiLink asChild underline="hover" className="pl-1">
          <Link to={communityDetailPath(community.id, community.externalFullId)} target="_blank">
            <span className="font-bold">
              {truncateText(community.name, MAX_COMMUNITY_NAME_LENGTH)}
            </span>
          </Link>
        </MuiLink>
      </p>
      <MuiTooltip
        title="Community name is pulled from JDE. Name can only be edited in JDE."
        side="bottom"
        arrow
        maxWidthPx={160}
        asChild
      >
        <span className="text-mui-grey-600 inline-flex">
          <MuiInfoOutlinedSmallIcon />
        </span>
      </MuiTooltip>
    </div>
  );
}

/**
 * The one banner the viewed version shows, if any — `LatestDraftVersionBanner`,
 * `LastVersionWarning` and `LatestPublishedVersionBanner` — drawn by
 * `SpecVersionAlert` at the signed-in offset, its button to that version.
 */
function SpecBanner({
  summary,
  version,
}: {
  summary: SpecDetailsSummary;
  version: SpecDetailsVersion;
}) {
  const banner = bannerFor(summary, version);
  if (!banner) return null;
  return (
    <SpecVersionAlert
      banner={banner}
      link={
        <Link to={specDetailPath(summary.id, banner.targetVersionId)}>{banner.actionLabel}</Link>
      }
      shownFor={`${version.id}:${summary.lastPublishedVersionId}:${summary.newestVersionId}`}
      placement="signed-in"
    />
  );
}

/** The alert's top offset on each face (`LastVersionWarning.tsx:58-70`). */
const ALERT_TOP = { "signed-in": "top-[125px]", public: "top-[40px]" } as const;

/**
 * A version alert — the donor's banners, one drawing for both faces:
 *
 *   - MUI's standard `Alert` at the chosen severity (the past-version icon in
 *     CSS `orange`), with MUI's close button;
 *   - its content a flex row, gap 16, the message then the button — a small,
 *     outlined, `color='inherit'` router link, unwrapped — wrapping and
 *     centred below 900px;
 *   - **floating**: absolute, `left 50%`, `translateX(-50%)`, anchored to the
 *     document — `top 125px` on the signed-in page, where it lands over the
 *     band, and `top 40px` on the public page, over the public header. The
 *     donor's `z-index: 1202` sits one over its app bar (1201) and under MUI's
 *     modal layer (1300), so an open Version menu covers it. Atlas's chrome and
 *     its portalled layers share `z-50`, where the document order decides: this
 *     comes after the header and the band, so it paints over them, and every
 *     portal comes after it, so the menu covers it as the donor's does;
 *   - dismissed for this page view; a change of `shownFor` (the version and
 *     pointers it was shown for) brings it back, as the donor's `useEffect`
 *     re-opens it.
 */
export function SpecVersionAlert({
  banner,
  link,
  shownFor,
  placement,
}: {
  banner: VersionBanner;
  /** The button: a router link to the version, labelled `banner.actionLabel`. */
  link: React.ReactElement;
  shownFor: string;
  placement: keyof typeof ALERT_TOP;
}) {
  const [dismissedFor, setDismissedFor] = React.useState<string | null>(null);
  if (dismissedFor === shownFor) return null;

  return (
    <MuiAlert
      severity={banner.severity}
      iconClassName={banner.severity === "warning" ? "text-mui-alert-orange" : undefined}
      onClose={() => setDismissedFor(shownFor)}
      data-testid="spec-version-banner"
      className={cn("absolute left-1/2 z-50 -translate-x-1/2", ALERT_TOP[placement])}
    >
      <div className="flex w-full flex-wrap items-center justify-between gap-4 text-center min-[900px]:flex-nowrap min-[900px]:text-left">
        <div className="w-full flex-auto min-[900px]:w-auto">{banner.message}</div>
        <div className="flex w-full flex-none justify-center min-[900px]:w-auto min-[900px]:justify-end">
          <MuiButton
            asChild
            variant="outlined"
            size="small"
            className="min-w-fit border-current whitespace-nowrap text-inherit hover:border-current hover:bg-black/4 focus-visible:border-current"
          >
            {link}
          </MuiButton>
        </div>
      </div>
    </MuiAlert>
  );
}
